// Moved from apps/admin/src/charts (D-066): shared by the vendor admin and the owner console.
// Shared admin charts (D-038/D-039): the KPI sparkline and the dashed-grid line/bar chart, both drawn
// with the logo gradient. Used by the Today dashboard and Reports.
import { useEffect, useId, useRef, useState } from "react";

/** The rendered width of an element (for charts drawn at their real size). */
export function useWidth<T extends HTMLElement>(fallback: number) {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(fallback);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(([e]) => {
      const w = Math.round(e?.contentRect.width ?? 0);
      if (w > 0) setWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width] as const;
}

// ---------------------------------------------------------------- sparkline

export function Sparkline({
  values,
  variant,
  label,
}: {
  values: number[];
  variant: "line" | "dots" | "gradient";
  label: string;
}) {
  const gid = `spark${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
  const w = 300;
  const h = 28;
  const pad = 4;
  const max = Math.max(...values, 0);
  const pts = (values.length > 1 ? values : [values[0] ?? 0, values[0] ?? 0]).map((v, i, a) => [
    pad + (i * (w - pad * 2)) / (a.length - 1),
    max > 0 ? h - pad - (v / max) * (h - pad * 2) : h - pad,
  ]);
  const d = pts.map(([x, y], i) => `${i ? "L" : "M"}${x!.toFixed(1)} ${y!.toFixed(1)}`).join(" ");
  const last = pts[pts.length - 1]!;
  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      className="mt-auto h-7 w-full overflow-visible"
      role="img"
      aria-label={label}
    >
      {/* D-039: the logo gradient runs along the trend (blue → green). */}
      <defs>
        <linearGradient id={gid} gradientUnits="userSpaceOnUse" x1="0" x2={w} y1="0" y2="0">
          <stop
            offset="0"
            stopColor="var(--ady-color-brand-blue)"
            stopOpacity={variant === "gradient" ? 0.15 : 1}
          />
          <stop offset="1" stopColor="var(--ady-color-brand-green)" />
        </linearGradient>
      </defs>
      <path
        d={d}
        fill="none"
        stroke={variant === "dots" ? "var(--ady-color-chart-3)" : `url(#${gid})`}
        strokeWidth={variant === "dots" ? 2.5 : 2}
        strokeDasharray={variant === "dots" ? "4 8" : undefined}
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
      {variant !== "gradient" ? (
        <circle
          cx={last[0]}
          cy={last[1]}
          r={3}
          fill={variant === "dots" ? "var(--ady-color-chart-3)" : "var(--ady-color-brand-green)"}
          vectorEffect="non-scaling-stroke"
        />
      ) : null}
    </svg>
  );
}

// ---------------------------------------------------------------- charts

export function niceStep(max: number): number {
  const raw = max / 4;
  const pow = 10 ** Math.floor(Math.log10(Math.max(raw, 1)));
  const n = raw / pow;
  return (n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10) * pow;
}

/** Dashed y grid (at least 0–4), x labels, and either one line with markers or grouped bars. */
export function Chart({
  kind,
  groups,
  labels,
  showLabel,
  series,
  label,
  width = 560,
  height = 300,
}: {
  kind: "line" | "bars";
  /** One array per series, same length as labels. */
  groups: number[][];
  labels: string[];
  showLabel: (i: number) => boolean;
  series: string[];
  label: string;
  /** Drawing size in px; match the rendered width so the axis text stays 11 px. */
  width?: number;
  height?: number;
}) {
  const w = width;
  const h = height;
  const bottom = 28;
  const top = 10;
  const max = Math.max(4, ...groups.flat());
  const step = niceStep(max);
  const yMax = Math.ceil(max / step) * step;
  const ticks = Array.from({ length: Math.round(yMax / step) + 1 }, (_, i) => i * step);
  const tickText = (v: number) => v.toLocaleString("en-IN");
  const left = 14 + Math.max(...ticks.map((v) => tickText(v).length)) * 7;
  const plotW = w - left - 8;
  const plotH = h - bottom - top;
  const slot = plotW / labels.length;
  const y = (v: number) => top + plotH - (v / yMax) * plotH;
  const cx = (i: number) => left + i * slot + slot / 2;
  const gid = `chart${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
  const colours = [`url(#${gid})`, "var(--ady-color-tone-action-border)"];
  const barW = Math.max(2, Math.min(28, (slot * 0.6) / groups.length));
  const line = groups[0] ?? [];
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="h-auto w-full" role="img" aria-label={label}>
      <defs>
        <linearGradient id={gid} gradientUnits="userSpaceOnUse" x1={left} x2={w - 8} y1="0" y2="0">
          <stop offset="0" stopColor="var(--ady-color-brand-blue)" />
          <stop offset="1" stopColor="var(--ady-color-brand-green)" />
        </linearGradient>
      </defs>
      {ticks.map((v) => (
        <g key={v}>
          <line
            x1={left}
            x2={w - 8}
            y1={y(v)}
            y2={y(v)}
            stroke="var(--ady-color-border-subtle)"
            strokeDasharray="3 4"
          />
          <text x={left - 10} y={y(v) + 4} textAnchor="end" className="fill-fg-tertiary text-2xs">
            {tickText(v)}
          </text>
        </g>
      ))}
      {kind === "line" ? (
        <>
          <path
            d={line
              .map((v, i) => `${i ? "L" : "M"}${cx(i).toFixed(1)} ${y(v).toFixed(1)}`)
              .join(" ")}
            fill="none"
            stroke={colours[0]}
            strokeWidth={2}
          />
          {line.map((v, i) => (
            <circle key={labels[i]} cx={cx(i)} cy={y(v)} r={3.5} fill={colours[0]}>
              <title>{`${labels[i]} · ${series[0]}: ${v}`}</title>
            </circle>
          ))}
        </>
      ) : (
        labels.map((lab, i) => {
          const x0 = cx(i) - (barW * groups.length) / 2;
          return (
            <g key={lab}>
              {groups.map((g, s) => {
                const v = g[i] ?? 0;
                return (
                  <rect
                    key={series[s]}
                    x={x0 + s * barW}
                    y={y(v)}
                    width={barW - 2}
                    height={top + plotH - y(v)}
                    rx={3}
                    fill={colours[s]}
                  >
                    <title>{`${lab} · ${series[s]}: ${tickText(v)}`}</title>
                  </rect>
                );
              })}
            </g>
          );
        })
      )}
      {labels.map((lab, i) =>
        showLabel(i) ? (
          <text
            key={lab}
            x={cx(i)}
            y={h - 8}
            textAnchor="middle"
            className="fill-fg-tertiary text-2xs"
          >
            {lab}
          </text>
        ) : null,
      )}
    </svg>
  );
}
